"use client";

import { Instagram, Youtube, Music, BookOpen } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuList() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="flex flex-col gap-3">
        {[0, 1, 2, 3].map(i => <li key={i} data-index={i}>
            <div className="h-4 w-28 bg-muted/40 animate-pulse rounded-md" />
          </li>)}
      </ul>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="text-neutral-gray hover:text-stark-white transition-colors duration-200 text-sm font-default">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Archive() {
  const listenLinks = [{
    label: "Gallery",
    to: "/GalleryHomePage"
  }, {
    label: "The Artist",
    to: "/TheArtistPage"
  }, {
    label: "Inquiries",
    to: "/InquiriesPage"
  }, {
    label: "Shop",
    to: "/ShopPage"
  }];
  const socials = [{
    label: "Instagram",
    Icon: Instagram,
    to: "#"
  }, {
    label: "YouTube",
    Icon: Youtube,
    to: "#"
  }, {
    label: "Spotify",
    Icon: Music,
    to: "#"
  }, {
    label: "Substack",
    Icon: BookOpen,
    to: "#"
  }];
  const tickerLanguages = ["Limited Production", "Ultra High End", "Film Photography", "Museum Quality", "Editorial Vision", "John John Fine Photography"];
  return <section data-section-id="1867" id="the-archive-footer" className="bg-base-black text-stark-white relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 w-full overflow-hidden leading-none -translate-y-px">
        <svg viewBox="0 0 1440 24" preserveAspectRatio="none" className="block w-full h-6" fill="var(--background)" aria-hidden="true">
          <path d="M0,24 L0,8 L40,14 L85,4 L130,16 L180,6 L230,18 L280,9 L335,15 L390,3 L445,13 L500,7 L560,17 L615,5 L670,15 L725,8 L785,18 L840,6 L900,14 L955,3 L1015,16 L1075,7 L1130,15 L1190,5 L1250,17 L1310,8 L1370,14 L1410,5 L1440,12 L1440,24 Z" />
        </svg>
      </div>

      <div className="relative max-w-[1320px] mx-auto px-8 md:px-16 py-24 md:py-32">
        {}
        <div className="flex items-center gap-3 mb-16">
          <span className="font-mono uppercase tracking-[0.18em] text-[11px] text-neutral-gray">
            [00:00:00:00] — THE ARCHIVE
          </span>
          <span className="inline-block w-2 h-2 rotate-45 bg-stark-white" aria-hidden="true" />
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          {}
          <div className="md:col-span-4 flex flex-col gap-6">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-8 w-auto text-stark-white" />
            </Link>

            <p className="font-default italic text-lg leading-relaxed text-stark-white/85 max-w-sm">
              Limited production, ultra high end film fine photography.
            </p>

            <p className="font-default italic text-sm text-neutral-gray">
              — established in the pursuit of light, 2025
            </p>

            {}
            <svg className="absolute left-0 bottom-32 w-32 h-32 opacity-[0.08] pointer-events-none hidden md:block" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" aria-hidden="true">
              <path d="M50,10 Q48,40 50,90" />
              <path d="M50,25 Q35,28 30,22" />
              <path d="M50,35 Q65,38 70,30" />
              <path d="M50,48 Q33,52 28,44" />
              <path d="M50,58 Q67,62 72,54" />
              <path d="M50,70 Q35,74 30,66" />
            </svg>
          </div>

          {}
          <div className="md:col-span-3 flex flex-col gap-5">
            <h3 className="font-mono uppercase tracking-[0.18em] text-[11px] text-neutral-gray font-default">
              Navigation
            </h3>
            <MenuProvider menu_id="25">
              <FooterMenuList />
            </MenuProvider>
          </div>

          {}
          <div className="md:col-span-3 flex flex-col gap-5">
            <h3 className="font-mono uppercase tracking-[0.18em] text-[11px] text-neutral-gray font-default">
              Explore
            </h3>
            <ul className="flex flex-col gap-3">
              {listenLinks.map((link, i) => <li data-index={i} key={link.label}>
                  <Link to={link.to} className="text-neutral-gray hover:text-stark-white transition-colors duration-200 text-sm font-default">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="md:col-span-2 flex flex-col gap-5">
            <h3 className="font-mono uppercase tracking-[0.18em] text-[11px] text-neutral-gray font-default">
              Connect
            </h3>
            <div className="grid grid-cols-2 gap-3 max-w-[120px]">
              {socials.map((social, i) => {
              const Icon = social.Icon;
              return <Button asChild key={social.label} variant="ghost" size="icon" data-index={i} className="h-11 w-11 border border-border hover:border-stark-white hover:bg-transparent hover:text-stark-white text-neutral-gray transition-all duration-200">
                    <Link to={social.to} aria-label={social.label}>
                      <Icon className="size-[18px]" strokeWidth={1.25} />
                    </Link>
                  </Button>;
            })}
            </div>
          </div>
        </div>

        {}
        <div className="mt-20 mb-8 flex items-center gap-2">
          <Separator className="flex-1 bg-border" />
          <span className="inline-block w-1.5 h-1.5 bg-stark-white" aria-hidden="true" />
        </div>

        {}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="font-mono uppercase tracking-[0.18em] text-[11px] text-neutral-gray">
            © 2025 JOHN JOHN FINE PHOTOGRAPHY{" "}
            <span className="text-stark-white mx-1">◆</span> ALL RIGHTS RESERVED
          </p>
          <div className="flex items-center gap-5 font-mono uppercase tracking-[0.18em] text-[11px] text-neutral-gray">
            <Link to="#" className="hover:text-stark-white transition-colors duration-200">
              Privacy
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="#" className="hover:text-stark-white transition-colors duration-200">
              Terms
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="#" className="hover:text-stark-white transition-colors duration-200">
              Cookies
            </Link>
          </div>
        </div>
      </div>

      {}
      <div className="relative w-full overflow-hidden border-t border-border py-4 bg-charcoal-depth">
        <div className="flex gap-10 whitespace-nowrap font-mono uppercase tracking-[0.18em] text-[11px] text-neutral-gray" style={{
        animation: "polyglotTicker 60s linear infinite",
        width: "max-content"
      }}>
          {[...tickerLanguages, ...tickerLanguages, ...tickerLanguages].map((lang, i) => <span data-index={i} key={i} className="flex items-center gap-10">
                {lang}
                <span className="text-stark-white" aria-hidden="true">
                  ·
                </span>
              </span>)}
        </div>
        <style>{`
          @keyframes polyglotTicker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.3333%); }
          }
        `}</style>
      </div>
    </section>;
}